// 10 NLP-style questions. Each option carries a hidden score map.
// Score keys: V (visual), A (auditory), K (kinesthetic), T (toward), AF (away_from), I (internal), E (external)

export const QUESTIONS = [
  {
    id: 1,
    text: "When you imagine your birth going well, what's the first thing that comes to mind?",
    options: [
      { label: "I picture a calm, beautiful scene", score: { V: 2, T: 1 } },
      { label: "I hear myself breathing and feel in control", score: { K: 2, I: 1 } },
      { label: "I feel safe and held by the people around me", score: { K: 1, E: 2 } },
      { label: "I replay inspiring birth stories I've heard", score: { A: 2, T: 1 } },
    ],
  },
  {
    id: 2,
    text: "When you're learning something new and important, what works best for you?",
    options: [
      { label: "Watching videos, reading books, seeing diagrams", score: { V: 2 } },
      { label: "Doing, practising, moving through it physically", score: { K: 2, I: 1 } },
      { label: "Listening to podcasts or talking it through", score: { A: 2, E: 1 } },
      { label: "Reading deeply, researching, taking notes", score: { V: 1, I: 2 } },
    ],
  },
  {
    id: 3,
    text: "What's your biggest driver when it comes to preparing for birth?",
    options: [
      { label: "I want to feel ready for whatever comes", score: { T: 1, I: 1 } },
      { label: "I'm scared of being caught off guard or things going wrong", score: { AF: 2 } },
      { label: "I want to give my baby the most beautiful start", score: { T: 2 } },
      { label: "I want to avoid feeling powerless or out of control", score: { AF: 2, I: 1 } },
    ],
  },
  {
    id: 4,
    text: "You're deciding on a birth support option. How do you make the call?",
    options: [
      { label: "I go with my gut. It'll feel right or it won't", score: { I: 2 } },
      { label: "I ask trusted friends or family who've been there", score: { E: 2 } },
      { label: "I research until I feel confident in the evidence", score: { I: 2, AF: 1 } },
      { label: "I wait to hear what my midwife recommends", score: { E: 2 } },
    ],
  },
  {
    id: 5,
    text: "How does uncertainty in pregnancy sit with you?",
    options: [
      { label: "I try to lean into the mystery and trust the process", score: { T: 2, I: 1 } },
      { label: "It makes me want to learn everything I possibly can", score: { AF: 2, I: 1 } },
      { label: "I need a trusted person to help me feel anchored", score: { E: 2 } },
      { label: "I focus on what I can control and let the rest go", score: { I: 2, AF: 1 } },
    ],
  },
  {
    id: 6,
    text: "When you imagine needing support during labour, what feels most helpful?",
    options: [
      { label: "Quiet encouragement. Trust that I know what to do", score: { I: 2 } },
      { label: "A familiar voice speaking affirmations aloud", score: { A: 2, E: 1 } },
      { label: "Physical touch. A hand, a squeeze, being held", score: { K: 2, E: 1 } },
      { label: "Knowing my support person has a clear plan", score: { E: 2 } },
    ],
  },
  {
    id: 7,
    text: "What kind of birth stories resonate with you most?",
    options: [
      { label: "Raw and real. Women who tapped into their power", score: { K: 1, T: 1, I: 1 } },
      { label: "Empowering and transformational. She rose to it", score: { T: 2 } },
      { label: "Peaceful and intentional. Beautifully planned", score: { V: 2 } },
      { label: "Honest and informative. The full picture, nothing hidden", score: { AF: 2, I: 1 } },
    ],
  },
  {
    id: 8,
    text: "After a hard moment in pregnancy, what helps you feel okay again?",
    options: [
      { label: "Sitting quietly with myself and reconnecting", score: { I: 2, V: 1 } },
      { label: "Talking it through with someone I trust", score: { A: 2, E: 2 } },
      { label: "Doing something. Moving my body, nesting, taking action", score: { K: 2 } },
      { label: "Researching and understanding what just happened", score: { AF: 2, I: 1 } },
    ],
  },
  {
    id: 9,
    text: "When you think about the postpartum period, what matters most to you?",
    options: [
      { label: "Having a community around me. I don't want to do this alone", score: { E: 2, T: 1 } },
      { label: "Having space to find my own rhythm as a mother", score: { I: 2 } },
      { label: "Knowing the practical plan in advance", score: { E: 1, AF: 2 } },
      { label: "Feeling emotionally prepared for how hard it might be", score: { AF: 2, A: 1 } },
    ],
  },
  {
    id: 10,
    text: "How do you feel about the idea of birth being unpredictable?",
    options: [
      { label: "I find it beautiful. Birth knows what it's doing", score: { T: 2, I: 2 } },
      { label: "It worries me. I want as much preparation as possible", score: { AF: 2, E: 1 } },
      { label: "I'm okay with it if I have the right person beside me", score: { E: 2 } },
      { label: "I focus on what I can control and accept the rest", score: { I: 2, AF: 1 } },
    ],
  },
];

export function tallyScores(answers) {
  // answers: array of selected option indexes (length 10)
  const totals = { V: 0, A: 0, K: 0, T: 0, AF: 0, I: 0, E: 0 };
  answers.forEach((optIdx, qIdx) => {
    if (optIdx == null) return;
    const opt = QUESTIONS[qIdx]?.options[optIdx];
    if (!opt) return;
    Object.entries(opt.score).forEach(([k, v]) => {
      totals[k] = (totals[k] || 0) + v;
    });
  });
  return totals;
}

export function determineArchetype(totals) {
  const VAK_pairs = [
    ['V', totals.V || 0],
    ['A', totals.A || 0],
    ['K', totals.K || 0],
  ].sort((a, b) => b[1] - a[1]);
  const VAK = VAK_pairs[0][0];
  const MOT = (totals.T || 0) >= (totals.AF || 0) ? 'T' : 'AF';
  const REF = (totals.I || 0) >= (totals.E || 0) ? 'I' : 'E';

  // Specific combinations first
  if (VAK === 'V' && MOT === 'T' && REF === 'I') return 'visionary';
  if (VAK === 'K' && MOT === 'AF' && REF === 'I') return 'warrior';
  if (VAK === 'K' && MOT === 'T' && REF === 'E') return 'feeler';
  if (VAK === 'A' && MOT === 'T' && REF === 'I') return 'oracle';
  // Scholar: Away From + Internal (any VAK), if not Warrior
  if (MOT === 'AF' && REF === 'I') return 'scholar';
  // Nurturer: Toward + External (any VAK), if not Feeler
  if (MOT === 'T' && REF === 'E') return 'nurturer';

  // Fallbacks
  if (REF === 'I' && MOT === 'T') return 'visionary';
  if (REF === 'I' && MOT === 'AF') return 'warrior';
  if (REF === 'E' && MOT === 'T') return 'feeler';
  return 'oracle';
}
